import React, { Component, type ReactNode } from 'react';
import monitoring from '../services/monitoring';
import { Button, Typography } from './ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Error Boundary component for catching and handling React errors
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to our monitoring service
    monitoring.captureError({
      message: `React Error: ${error.message}`,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      severity: 'high',
      tags: {
        type: 'react_error',
        component: 'ErrorBoundary',
      },
      extra: {
        errorInfo,
        errorBoundary: true,
      },
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Add breadcrumb for debugging
    monitoring.addBreadcrumb('React Error Boundary caught an error', 'error', {
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  handleRetry = () => {
    // Reset error state
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });

    // Track retry action
    monitoring.trackUserInteraction('error_boundary_retry', 'click', {
      error_message: this.state.error?.message,
    });
  };

  handleReportIssue = () => {
    // Track issue reporting
    monitoring.trackUserInteraction('error_boundary_report', 'click', {
      error_message: this.state.error?.message,
    });

    // In a real app, this could open a support form or email
    const subject = encodeURIComponent('Error Report: React Application Error');
    const body = encodeURIComponent(
      `
Error Message: ${this.state.error?.message || 'Unknown error'}
Stack Trace: ${this.state.error?.stack || 'Not available'}
Component Stack: ${this.state.errorInfo?.componentStack || 'Not available'}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}
    `.trim()
    );

    window.open(`mailto:support@example.com?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div
          className="min-h-screen flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--bg-primary)' }}
        >
          <div
            className="max-w-md w-full rounded-lg shadow-lg p-6"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              boxShadow: 'var(--shadow)',
            }}
          >
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">⚠️</div>
              <Typography variant="h2" className="mb-2">
                Oops! Something went wrong
              </Typography>
              <Typography variant="body1" color="secondary">
                We're sorry, but something unexpected happened. The error has
                been reported and we'll look into it.
              </Typography>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details
                className="mb-6 rounded-lg p-4"
                style={{ backgroundColor: 'var(--bg-tertiary)' }}
              >
                <summary
                  className="cursor-pointer font-medium hover:opacity-80"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Error Details (Development)
                </summary>
                <div className="mt-4 space-y-4">
                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Error Message:
                    </h4>
                    <pre
                      className="p-3 rounded text-sm overflow-x-auto"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        border: `1px solid var(--border)`,
                      }}
                    >
                      {this.state.error.message}
                    </pre>
                  </div>

                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Stack Trace:
                    </h4>
                    <pre
                      className="p-3 rounded text-xs overflow-x-auto max-h-32"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-secondary)',
                        border: `1px solid var(--border)`,
                      }}
                    >
                      {this.state.error.stack}
                    </pre>
                  </div>

                  {this.state.errorInfo && (
                    <div>
                      <h4
                        className="font-semibold mb-2"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Component Stack:
                      </h4>
                      <pre
                        className="p-3 rounded text-xs overflow-x-auto max-h-32"
                        style={{
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-secondary)',
                          border: `1px solid var(--border)`,
                        }}
                      >
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="space-y-3">
              <Button
                onClick={this.handleRetry}
                variant="primary"
                className="w-full"
              >
                Try Again
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={this.handleReportIssue} variant="secondary">
                  Report Issue
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="secondary"
                >
                  Refresh Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
